/**
 * VoiceStatusLine — Replaces the readonly Textarea during voice sessions.
 *
 * Shows an animated status dot + status text + optional live transcript.
 * Color-coded per voice state (listening=green, thinking=amber, speaking=blue).
 *
 * DevPlan 0054 — Tasks 3, 4, 6
 */

import { cn } from '@/lib/utils';
import type { VoiceStatus } from '@/hooks/useVoiceConversation';

// ── Props ─────────────────────────────────────────────────────────────────

interface VoiceStatusLineProps {
  status: VoiceStatus;
  /** Live transcript text scrolling alongside the status. */
  transcript?: string;
  /** Compact mode: only dot + text, no transcript (e.g. HelpFab). */
  compact?: boolean;
  className?: string;
}

// ── Status labels ─────────────────────────────────────────────────────────

const STATUS_TEXT: Partial<Record<VoiceStatus, string>> = {
  connecting: 'Verbinde…',
  listening: 'Sprich jetzt…',
  thinking: 'KI verarbeitet…',
  speaking: 'KI spricht…',
};

// ── Wave Bars (inline, small) ─────────────────────────────────────────────

function InlineWaveBars() {
  return (
    <span aria-hidden className="inline-flex items-center gap-[1.5px] ml-1.5">
      {Array.from({ length: 4 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'inline-block w-[2px] rounded-full bg-voice-speaking',
            'animate-[voice-orb-wave_1s_ease-in-out_infinite]',
            'motion-reduce:animate-none',
          )}
          style={{ height: '10px', animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export function VoiceStatusLine({
  status,
  transcript,
  compact = false,
  className,
}: VoiceStatusLineProps) {
  const label = STATUS_TEXT[status] ?? '';

  return (
    <div
      className={cn(
        'flex items-center gap-2 min-h-[36px] px-1 py-1 flex-1',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {/* Animated status dot */}
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 shrink-0 rounded-full transition-colors',
          status === 'connecting' && 'bg-muted-foreground/40 animate-pulse motion-reduce:animate-none',
          status === 'listening' && 'bg-voice-listening animate-pulse motion-reduce:animate-none',
          status === 'thinking' && 'bg-voice-thinking animate-pulse motion-reduce:animate-none',
          status === 'speaking' && 'bg-voice-speaking animate-pulse motion-reduce:animate-none',
        )}
      />

      {/* Status text */}
      <span
        className={cn(
          'text-sm font-medium whitespace-nowrap',
          status === 'connecting' && 'text-muted-foreground',
          status === 'listening' && 'text-voice-listening',
          status === 'thinking' && 'text-voice-thinking',
          status === 'speaking' && 'text-voice-speaking',
        )}
      >
        {label}
      </span>

      {/* Wave bars for speaking state */}
      {status === 'speaking' && <InlineWaveBars />}

      {/* Live transcript (truncated, right-aligned) */}
      {!compact && transcript && status === 'listening' && (
        <span className="ml-auto max-w-[200px] truncate text-xs text-muted-foreground italic">
          {transcript}
        </span>
      )}
    </div>
  );
}
